import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useDisclosure } from "@/shared/hooks/useDisclosure";
import { formatDateLocal, parseISO, toISOForAPI } from "@/shared/lib/date-time";
import {
  Badge,
  Button,
  Card,
  CardContent,
  createSortableColumn,
  DataTable,
  Modal,
  type TanstackReactTable,
} from "@devhop/ui";
import { useNavigate } from "@tanstack/react-router";
import {
  AlertCircleIcon,
  BellIcon,
  CalendarIcon,
  DownloadIcon,
  EyeIcon,
  HelpCircle,
  RefreshCwIcon,
} from "lucide-react";
import type { ContractDTO } from "../../domain/contracts";
import { useExpiringContractsQuery, useRenewContract } from "../api/queries";
import {
  ContractExpiryTour,
  useContractExpiryTour,
} from "../tour/ContractExpiryTour";
import { ContractRenewalForm } from "../ui/ContractRenewalForm";

type ExpiringContract = ContractDTO;

function calculateDaysUntilExpiry(endDate: string | Date): number {
  const end = new Date(endDate).getTime();
  const now = Date.now();
  const diff = end - now;
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getStatusBadge(days: number, category: "30" | "60" | "90") {
  if (category === "30") {
    return (
      <Badge variant="destructive" className="gap-1">
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {days} ວັນ
      </Badge>
    );
  }
  if (category === "60") {
    return (
      <Badge variant="default" className="gap-1 bg-orange-500 text-white">
        <span className="h-1.5 w-1.5 rounded-full bg-current" />
        {days} ວັນ
      </Badge>
    );
  }
  return (
    <Badge variant="secondary" className="gap-1">
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {days} ວັນ
    </Badge>
  );
}

export function ContractExpiryRenewalPage() {
  const tour = useContractExpiryTour();
  const nav = useNavigate();
  const canRenew = useActionPermission(["contracts:renew"]);
  const canView = useActionPermission(["contracts:read"]);
  const renewalModal = useDisclosure<{
    id: string;
    title: string;
    endDate: string;
  }>();

  // 30-day: contracts expiring in 0-30 days
  const list30 = useExpiringContractsQuery({
    offset: 0,
    limit: 100,
    days: 30,
    excludeDays: 0,
  });

  // 60-day: contracts expiring in 31-60 days (exclude 0-30)
  const list60 = useExpiringContractsQuery({
    offset: 0,
    limit: 100,
    days: 60,
    excludeDays: 30,
  });

  // 90-day: contracts expiring in 61-90 days (exclude 0-60)
  const list90 = useExpiringContractsQuery({
    offset: 0,
    limit: 100,
    days: 90,
    excludeDays: 60,
  });

  const expired = useExpiringContractsQuery({
    offset: 0,
    limit: 100,
    days: -1, // Past contracts
  });

  const criticalCount = list30.data?.meta?.total ?? 0;
  const upcomingCount = list60.data?.meta?.total ?? 0;
  const routineCount = list90.data?.meta?.total ?? 0;
  const archivedCount = expired.data?.meta?.total ?? 0;

  const renewContract = useRenewContract(renewalModal.data?.id ?? "");

  const createColumns = (
    category: "30" | "60" | "90" | "expired",
  ): TanstackReactTable.ColumnDef<ExpiringContract>[] => [
    createSortableColumn<ExpiringContract>("title", "ຊື່ສັນຍາ", { size: 250 }),
    {
      id: "typeName",
      header: "ປະເພດ",
      cell: ({ row }) => (
        <Badge variant="secondary" className="text-xs">
          {row.original.typeName || "-"}
        </Badge>
      ),
      size: 120,
    },
    createSortableColumn<ExpiringContract>("counterparty", "ຄູ່ສັນຍາ", {
      size: 180,
    }),
    {
      id: "endDate",
      header: category === "expired" ? "ວັນທີສິ້ນສຸດ" : "ວັນທີຫມົດອາຍຸ",
      cell: ({ row }) => {
        const days = calculateDaysUntilExpiry(row.original.endDate);
        const dateStr = formatDateLocal(row.original.endDate);
        if (category === "expired") {
          return (
            <span className="text-muted-foreground text-sm">{dateStr}</span>
          );
        }
        return (
          <div className="flex flex-col gap-1">
            <span
              className={`text-sm ${category === "30" ? "text-destructive" : category === "60" ? "text-orange-600" : ""}`}
            >
              {dateStr}
            </span>
            {getStatusBadge(days, category)}
          </div>
        );
      },
      size: 150,
    },
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const statusLabels: Record<
          string,
          { label: string; variant: "default" | "secondary" | "destructive" }
        > = {
          active: { label: "ກຳລັງໃຊ້ງານ", variant: "default" },
          expiring_soon: { label: "ໃກ້ຫມົດອາຍຸ", variant: "secondary" },
          expired: { label: "ຫມົດອາຍຸ", variant: "secondary" },
          renewing: { label: "ກຳລັງຕໍ່ອາຍຸ", variant: "default" },
          terminated: { label: "ສິ້ນສຸດ", variant: "secondary" },
          draft: { label: "ຮ່າງ", variant: "secondary" },
        };
        const contractStatus = row.original.status || "draft";
        const status = statusLabels[contractStatus] ?? statusLabels.draft;
        if (!status) return null;
        return (
          <Badge variant={status.variant} className="text-xs">
            {status.label}
          </Badge>
        );
      },
      size: 120,
    },
    {
      id: "responsiblePersonName",
      header: "ຜູ້ຮັບຜິດຊອບ",
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          {row.original.responsiblePersonName ? (
            <>
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 font-medium text-primary text-xs">
                {row.original.responsiblePersonName.charAt(0).toUpperCase()}
              </div>
              <span className="text-sm">
                {row.original.responsiblePersonName}
              </span>
            </>
          ) : (
            <span className="text-muted-foreground text-sm">-</span>
          )}
        </div>
      ),
      size: 150,
    },
    {
      size: 150,
      id: "actions",
      cell: ({ row }: { row: { original: ExpiringContract } }) => {
        if (category === "expired") {
          return (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => nav({ to: `/app/contracts/${row.original.id}` })}
            >
              ເບິ່ງ
            </Button>
          );
        }
        return (
          <div className="flex items-center gap-2">
            {canView && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => nav({ to: `/app/contracts/${row.original.id}` })}
              >
                <EyeIcon className="h-4 w-4" />
              </Button>
            )}
            {canRenew && (
              <Button
                variant="default"
                size="sm"
                onClick={() =>
                  renewalModal.openWith({
                    id: row.original.id,
                    title: row.original.title,
                    endDate:
                      toISOForAPI(row.original.endDate) ??
                      String(row.original.endDate),
                  })
                }
              >
                <RefreshCwIcon className="mr-1 h-4 w-4" />
                ຕໍ່ອາຍຸ
              </Button>
            )}
          </div>
        );
      },
    },
  ];

  const handleRenew = async (vals: {
    newEndDate: string | Date;
    notes?: string | null;
    renewalDocumentId?: string | null;
  }) => {
    if (!renewalModal.data) return;
    await renewContract.mutateAsync({
      newEndDate:
        vals.newEndDate instanceof Date
          ? vals.newEndDate
          : parseISO(String(vals.newEndDate)),
      notes: vals.notes ?? undefined,
      renewalDocumentId: vals.renewalDocumentId ?? undefined,
    });
    renewalModal.close();
    list30.refetch();
    list60.refetch();
    list90.refetch();
  };

  return (
    <>
      <Header />
      <Main>
        <ContractExpiryTour
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        {/* Page Header */}
        <div className="mb-6">
          <div className="mb-4 flex items-start justify-between">
            <div>
              <div className="mb-2 flex items-center gap-3">
                <h1 className="font-bold text-3xl">ການຕໍ່ອາຍຸ & ຕິດຕາມສັນຍາ</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 rounded-full"
                  onClick={tour.startTour}
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                ຕິດຕາມສັນຍາທີ່ໃກ້ຫມົດອາຍຸ ແລະ ຈັດການການຕໍ່ອາຍຸ
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div
          className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4"
          data-tourid="expiry-summary-cards"
        >
          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="mb-2 font-medium text-muted-foreground text-sm uppercase tracking-wide">
                    ສຳຄັນ (30 ວັນ)
                  </div>
                  <div className="mb-1 font-bold text-4xl">{criticalCount}</div>
                  <Badge variant="destructive" className="gap-1">
                    <AlertCircleIcon className="h-3 w-3" />
                    ດ່ວນ
                  </Badge>
                </div>
                <div className="rounded-full bg-destructive/10 p-3">
                  <BellIcon className="h-6 w-6 text-destructive" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="mb-2 font-medium text-muted-foreground text-sm uppercase tracking-wide">
                    ຈະມາເຖິງ (60 ວັນ)
                  </div>
                  <div className="mb-1 font-bold text-4xl">{upcomingCount}</div>
                  <Badge
                    variant="default"
                    className="gap-1 bg-orange-500 text-white"
                  >
                    <AlertCircleIcon className="h-3 w-3" />
                    ຕ້ອງການການດຳເນີນງານ
                  </Badge>
                </div>
                <div className="rounded-full bg-orange-500/10 p-3">
                  <AlertCircleIcon className="h-6 w-6 text-orange-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="mb-2 font-medium text-muted-foreground text-sm uppercase tracking-wide">
                    ປົກກະຕິ (90 ວັນ)
                  </div>
                  <div className="mb-1 font-bold text-4xl">{routineCount}</div>
                  <Badge variant="secondary" className="gap-1">
                    <CalendarIcon className="h-3 w-3" />
                    ສະຖຽນ
                  </Badge>
                </div>
                <div className="rounded-full bg-green-500/10 p-3">
                  <CalendarIcon className="h-6 w-6 text-green-500" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="mb-2 font-medium text-muted-foreground text-sm uppercase tracking-wide">
                    ເກັບຮັກສາ
                  </div>
                  <div className="mb-1 font-bold text-4xl">{archivedCount}</div>
                  <Badge variant="secondary" className="gap-1">
                    <DownloadIcon className="h-3 w-3" />
                    ຫມົດອາຍຸ
                  </Badge>
                </div>
                <div className="rounded-full bg-muted p-3">
                  <DownloadIcon className="h-6 w-6 text-muted-foreground" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Critical: Expiring in 30 Days */}
        {criticalCount > 0 && (
          <div className="mb-6" data-tourid="expiry-critical-table">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircleIcon className="h-5 w-5 text-destructive" />
                <h2 className="font-semibold text-xl">
                  ສຳຄັນ: ຈະຫມົດອາຍຸໃນ 30 ວັນ
                </h2>
              </div>
              <Badge variant="destructive">{criticalCount} ລາຍການ</Badge>
            </div>
            <Card>
              <CardContent className="p-0">
                <DataTable<ExpiringContract, unknown>
                  noDataMessage="ບໍ່ພົບສັນຍາທີ່ໃກ້ຫມົດອາຍຸ"
                  isLoading={list30.isLoading}
                  columns={createColumns("30")}
                  data={list30.data?.data ?? []}
                  offset={0}
                  limit={100}
                  totalCount={list30.data?.meta?.total ?? 0}
                  onPaginationChange={() => {}}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Upcoming: 60 Days */}
        {upcomingCount > 0 && (
          <div className="mb-6" data-tourid="expiry-upcoming-table">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertCircleIcon className="h-5 w-5 text-orange-500" />
                <h2 className="font-semibold text-xl">ຈະມາເຖິງ: 60 ວັນ</h2>
              </div>
              <Badge variant="default" className="bg-orange-500 text-white">
                {upcomingCount} ລາຍການ
              </Badge>
            </div>
            <Card>
              <CardContent className="p-0">
                <DataTable<ExpiringContract, unknown>
                  noDataMessage="ບໍ່ພົບສັນຍາ"
                  isLoading={list60.isLoading}
                  columns={createColumns("60")}
                  data={list60.data?.data ?? []}
                  offset={0}
                  limit={100}
                  totalCount={list60.data?.meta?.total ?? 0}
                  onPaginationChange={() => {}}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Routine: 90 Days */}
        {routineCount > 0 && (
          <div className="mb-6" data-tourid="expiry-routine-table">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CalendarIcon className="h-5 w-5 text-green-500" />
                <h2 className="font-semibold text-xl">ປົກກະຕິ: 90 ວັນ</h2>
              </div>
              <Badge variant="secondary">{routineCount} ລາຍການ</Badge>
            </div>
            <Card>
              <CardContent className="p-0">
                <DataTable<ExpiringContract, unknown>
                  noDataMessage="ບໍ່ພົບສັນຍາ"
                  isLoading={list90.isLoading}
                  columns={createColumns("90")}
                  data={list90.data?.data ?? []}
                  offset={0}
                  limit={100}
                  totalCount={list90.data?.meta?.total ?? 0}
                  onPaginationChange={() => {}}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Archived: Expired Contracts */}
        {archivedCount > 0 && (
          <div className="mb-6" data-tourid="expiry-archived-table">
            <div className="mb-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DownloadIcon className="h-5 w-5 text-muted-foreground" />
                <h2 className="font-semibold text-xl">ເກັບຮັກສາ: ສັນຍາທີ່ຫມົດອາຍຸ</h2>
              </div>
              <Badge variant="secondary">{archivedCount} ລາຍການ</Badge>
            </div>
            <Card>
              <CardContent className="p-0">
                <DataTable<ExpiringContract, unknown>
                  noDataMessage="ບໍ່ພົບສັນຍາທີ່ຫມົດອາຍຸ"
                  isLoading={expired.isLoading}
                  columns={createColumns("expired")}
                  data={expired.data?.data ?? []}
                  offset={0}
                  limit={100}
                  totalCount={expired.data?.meta?.total ?? 0}
                  onPaginationChange={() => {}}
                />
              </CardContent>
            </Card>
          </div>
        )}

        {/* Renewal Modal */}
        <Modal
          open={renewalModal.isOpen}
          onOpenChange={renewalModal.toggle}
          title="ຕໍ່ອາຍຸສັນຍາ"
          size="lg"
        >
          <div className="max-h-[calc(100vh-12rem)] overflow-y-auto">
            {renewalModal.data && (
              <ContractRenewalForm
                initialEndDate={renewalModal.data.endDate}
                contractTitle={renewalModal.data.title}
                onSubmit={handleRenew}
                submitting={renewContract.isPending}
                onCancel={() => renewalModal.close()}
              />
            )}
          </div>
        </Modal>
      </Main>
    </>
  );
}
